import { BlockEntitiesTable } from '@dealstack/frontend/shared/lib/BlockTable/BlockEntitiesTable.tsx';
import { EntityColorMapInput } from '@dealstack/frontend/shared/lib/BlockTable/BlockEntityCell/BlockEntities.types.ts';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import { HoldingDetailRes, HoldingEventDto } from '@dealstack/shared/lib/generated';
import { parseBEDateIgnoreTimezone } from '@dealstack/shared/lib/format/dates/date.ts';
import { useMemo } from 'react';
import { BlockEntityEvent } from '@dealstack/frontend/shared/lib/BlockTable/BlockEntitiesTable.types';

const orange6 = defaultTheme.v3.colors.orange[6];
const primary6 = defaultTheme.v3.colors.blue[6];
const teal8 = defaultTheme.v3.colors.teal[8];

const entityColorMap: EntityColorMapInput = {
	mainEntityColor: orange6,
	issuerEntityColor: primary6,
	otherEntityColor: teal8,
};

export const HoldingEventsLogTimeline = ({
	holdingEventsData = [],
	holdingDetails,
}: {
	holdingEventsData?: HoldingEventDto[] | null;
	holdingDetails?: HoldingDetailRes | null;
}) => {
	const tCommonUi = useTranslationCommonUi();

	const issuerId = holdingDetails?.issuerId ?? '';
	const mainEntityId = holdingDetails?.holderId ?? '';

	const headers = useMemo(() => {
		const { issuerName, holderName } = holdingDetails || {};
		return [
			{ label: '', color: 'transparent' },
			{ label: issuerName || 'Issuer', color: entityColorMap.issuerEntityColor },
			{ label: holderName || 'Holder', color: entityColorMap.mainEntityColor },
			{ label: tCommonUi('others'), color: entityColorMap.otherEntityColor },
		];
	}, [holdingDetails, tCommonUi]);

	const events = useMemo((): BlockEntityEvent[] => {
		return (holdingEventsData ?? []).map((event, i) => ({
			id: `${event.type}-${i}`, // only used for keys on FE
			transactionType: event.type,
			fromEntityId: event.source || null,
			toEntityId: event.destination || null,
			fromEntityName: event.sourceDisplayName,
			toEntityName: event.destDisplayName,
			fromBlocksHeld: event.sourceBlocksHeld,
			toBlocksHeld: event.destinationBlocksHeld,
			date: parseBEDateIgnoreTimezone(event.date),
			blocks: event.blocksMoving,
			units: Number(event.units),
			currency: event.securityCurrency,
			securityIssuedIn: event.securityIssuedIn,
			fromCost: event.sourceCost,
			toCost: event.destinationCost,
			transactionCost: event.transactionCost ?? 0,
			securityUnitDenominator: event.securityUnitDenominator,
		}));
	}, [holdingEventsData]);

	return (
		<BlockEntitiesTable
			headers={headers}
			events={events}
			issuerEntityId={issuerId}
			mainEntityId={mainEntityId}
			entityColorMap={entityColorMap}
		/>
	);
};
